import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import { useSound, NoteKey } from './hooks';
import { useCart } from '../../contexts/CartContext';
import { get } from '../../services/apiClient';

// Product from API (/api/v1/products)
interface ApiProduct {
  id: string;
  slug: string;
  category: string;
  name: Record<string, string>;
  shortDescription: Record<string, string>;
  description: Record<string, string>;
  images: { main: string; thumbnails: string[] };
  features: Record<string, string[]>;
  specifications: Record<string, unknown>;
  pricing: Record<string, number>;
  stock: number;
  inStock: boolean;
  leadTime: string;
}

// Internal product shape used by the page (superset of API + static JSON)
interface ProductItem {
  id: string;
  slug: string;
  name: Record<string, string>;
  shortDescription: Record<string, string>;
  description: Record<string, string>;
  categories: string[];
  images: { main: string; thumbnails: string[] };
  features: Record<string, string[]>;
  specifications: Record<string, unknown>;
  pricing: Record<string, number>;
  inStock: boolean;
  leadTime: string;
}

// Product data type
interface ProductData {
  category: {
    id: string;
    name: Record<string, string>;
    description: Record<string, string>;
  };
  products: ProductItem[];
}

// Nav notes for sound
const NAV_NOTES: NoteKey[] = ['E4', 'G4', 'A4', 'B4', 'C5'];

export default function ProductCategoryPage() {
  const { category } = useParams<{ category: string }>();
  const { t, i18n } = useTranslation('landing');
  const [productData, setProductData] = useState<ProductData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { playNote, soundEnabled, toggleSound } = useSound();
  const { addItem, items } = useCart();
  const [language, setLanguage] = useState(i18n.language || 'en');

  // Language change handler
  const handleSetLanguage = (lang: string) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem('i18n_language', lang);
  };

  // Nav items
  const navItems = [
    { label: t('nav.services'), href: '/#services', note: NAV_NOTES[0] },
    { label: t('nav.products'), href: '/#products', note: NAV_NOTES[1] },
    { label: t('nav.portfolio'), href: '/#portfolio', note: NAV_NOTES[2] },
    { label: t('nav.industries'), href: '/#industries', note: NAV_NOTES[3] },
    { label: t('nav.about'), href: '/#why', note: NAV_NOTES[4] || ('C6' as NoteKey) },
  ];

  // Load product data: try API first, fall back to static JSON
  useEffect(() => {
    const loadProductData = async () => {
      try {
        setLoading(true);

        // Try product API
        const apiProducts = await get<ApiProduct[]>(`/api/v1/products?category=${category}`);
        if (apiProducts && apiProducts.length > 0) {
          const products: ProductItem[] = apiProducts.map((p) => ({
            id: p.id,
            slug: p.slug,
            name: p.name,
            shortDescription: p.shortDescription,
            description: p.description,
            categories: [p.category],
            images: p.images || { main: '', thumbnails: [] },
            features: p.features || {},
            specifications: p.specifications || {},
            pricing: p.pricing,
            inStock: p.inStock,
            leadTime: p.leadTime || '',
          }));
          setProductData({
            category: {
              id: category!,
              name: products[0]?.name || {},
              description: {},
            },
            products,
          });
          setError(null);
          return;
        }
      } catch {
        // API not available — fall through to static JSON
      }

      // Fallback: static JSON
      try {
        const data = await import(`./data/products/${category}.json`);
        setProductData(data.default || data);
        setError(null);
      } catch {
        setError('Product category not found');
        setProductData(null);
      }
    };

    if (category) {
      loadProductData().finally(() => setLoading(false));
    }
  }, [category]);

  // Get localized text
  const getLocalizedText = (obj: Record<string, string> | undefined): string => {
    if (!obj) return '';
    return obj[language] || obj['en'] || '';
  };

  const getLocalizedArray = (obj: Record<string, string[]> | undefined): string[] => {
    if (!obj) return [];
    return obj[language] || obj['en'] || [];
  };

  // Format currency
  const formatPrice = (pricing: Record<string, number>): string => {
    const currency = language === 'zh-TW' ? 'TWD' : 'USD';
    const price = pricing[currency] || pricing['USD'];
    const symbol = currency === 'TWD' ? 'NT$' : '$';
    return `${symbol}${price.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--navy-deep)] flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[var(--cyan-electric)] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !productData) {
    return (
      <div className="min-h-screen bg-[var(--navy-deep)] text-white">
        <Navigation
          language={language}
          onSetLanguage={handleSetLanguage}
          soundEnabled={soundEnabled}
          onToggleSound={toggleSound}
          onPlayNote={playNote}
          navItems={navItems}
          contactLabel={t('nav.contact')}
          cartItemCount={items.reduce((s, i) => s + i.quantity, 0)}
        />
        <div className="pt-32 px-6 text-center">
          <h1 className="text-4xl font-bold mb-4">Category Not Found</h1>
          <p className="text-gray-400 mb-8">The product category "{category}" does not exist.</p>
          <Link
            to="/#products"
            className="px-6 py-3 bg-[var(--cyan-electric)] text-[var(--navy-deep)] rounded-lg font-medium hover:bg-cyan-400 transition-colors"
          >
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--navy-deep)] text-white">
      <Navigation
        language={language}
        onSetLanguage={handleSetLanguage}
        soundEnabled={soundEnabled}
        onToggleSound={toggleSound}
        onPlayNote={playNote}
        navItems={navItems}
        contactLabel={t('nav.contact')}
      />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumb */}
          <nav className="mb-8 flex items-center gap-2 text-sm text-gray-400">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/#products" className="hover:text-white transition-colors">Products</Link>
            <span>/</span>
            <span className="text-[var(--cyan-electric)]">{getLocalizedText(productData.category.name)}</span>
          </nav>

          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {getLocalizedText(productData.category.name)}
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl">
            {getLocalizedText(productData.category.description)}
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {productData.products.map((product) => (
              <div
                key={product.id}
                className="group bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 transition-all duration-300"
                onMouseEnter={() => playNote('E5')}
              >
                {/* Product Image */}
                <div className="aspect-video bg-gradient-to-br from-[var(--navy-mid)] to-[var(--navy-deep)] relative overflow-hidden">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg className="w-24 h-24 text-[var(--cyan-electric)]/30" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1">
                      <circle cx="24" cy="24" r="6" />
                      <circle cx="24" cy="24" r="14" strokeDasharray="4 4" />
                      <circle cx="24" cy="24" r="20" strokeDasharray="2 4" />
                    </svg>
                  </div>
                  {/* Stock Badge */}
                  <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-medium ${
                    product.inStock
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-amber-500/20 text-amber-400'
                  }`}>
                    {product.inStock ? 'In Stock' : product.leadTime}
                  </div>
                </div>

                {/* Product Info */}
                <div className="p-6">
                  {/* Categories */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {product.categories.slice(0, 3).map((cat) => (
                      <span key={cat} className="px-2 py-0.5 text-xs rounded-full bg-[var(--cyan-electric)]/20 text-[var(--cyan-electric)]">
                        {cat}
                      </span>
                    ))}
                  </div>

                  <h3 className="text-xl font-semibold mb-2 group-hover:text-[var(--cyan-electric)] transition-colors">
                    {getLocalizedText(product.name)}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {getLocalizedText(product.shortDescription)}
                  </p>

                  {/* Features Preview */}
                  <ul className="space-y-1 mb-4">
                    {getLocalizedArray(product.features).slice(0, 3).map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                        <span className="w-1.5 h-1.5 mt-1.5 rounded-full bg-[var(--cyan-electric)] flex-shrink-0" />
                        <span className="line-clamp-1">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Price & CTA */}
                  <div className="flex items-center justify-between pt-4 border-t border-white/10">
                    <div>
                      <span className="text-xs text-gray-500">Starting at</span>
                      <div className="text-xl font-bold text-[var(--cyan-electric)]">
                        {formatPrice(product.pricing)}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {product.inStock && (
                        <button
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-all"
                          onClick={() => {
                            addItem({
                              productId: product.id,
                              slug: product.slug,
                              name: product.name,
                              image: product.images?.main || '',
                              pricing: product.pricing,
                            });
                            playNote('C6');
                          }}
                        >
                          {t('cart.addToCart', 'Add to Cart')}
                        </button>
                      )}
                      <button
                        className="px-4 py-2 bg-[var(--cyan-electric)]/20 text-[var(--cyan-electric)] rounded-lg text-sm font-medium hover:bg-[var(--cyan-electric)] hover:text-[var(--navy-deep)] transition-all"
                        onClick={() => playNote('C6')}
                      >
                        {t('cart.learnMore', 'Learn More')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-[var(--cyan-electric)]/10 to-[var(--amber-warm)]/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Need a Custom Solution?</h2>
          <p className="text-gray-400 mb-8">
            Our engineering team can design and manufacture IoT solutions tailored to your specific requirements.
          </p>
          <Link
            to="/#contact"
            className="inline-block px-8 py-3 bg-[var(--cyan-electric)] text-[var(--navy-deep)] rounded-lg font-medium hover:bg-cyan-400 transition-colors"
          >
            Contact Sales
          </Link>
        </div>
      </section>

      <Footer
        companyName={t('footer.companyName')}
        companyNameZh={t('footer.companyNameZh')}
        slogan={t('footer.slogan')}
        copyright={t('footer.copyright')}
        navItems={navItems.map((item) => ({ label: item.label, href: item.href }))}
      />
    </div>
  );
}
